import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DistrictContacts from './DistrictContacts';

interface Summary {
  totalRegistrations: number;
  totalDelegates: number;
  totalAmount: number;
  pendingPayments: number;
  failedPayments: number;
}

interface Transaction {
  id: number;
  registration_id: number;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  amount: number;
  status: string;
  created_at: string;
  name: string;
  email: string;
  club_name: string;
  receipt_no: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Delegate {
  delegate_name: string;
  delegate_designation: string;
}

interface Registration {
  id: number;
  receipt_no: string;
  name: string;
  email: string;
  phone: string;
  club_name: string;
  delegate_count: number;
  total_amount: number;
  payment_status: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  created_at: string;
  delegates: Delegate[];
  email_status: string;
  whatsapp_status: string;
}

interface DesignationRegistration {
  person_name: string;
  club_name: string;
}

interface DesignationReportItem {
  designation: string;
  total_registered: number;
  registrations: DesignationRegistration[];
}

interface ClubDesignationRegistration {
  person_name: string;
  designation: string;
}

interface ClubDesignationReportItem {
  club_name: string;
  total_registered: number;
  registrations: ClubDesignationRegistration[];
}

interface LogEntry {
  id: number;
  timestamp: string;
  level: string;
  category: string;
  message: string;
  details: any;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'registrations' | 'designation' | 'district' | 'contacts' | 'logs' | 'settings'>('overview');
  const [registrationCloseDate, setRegistrationCloseDate] = useState('2026-05-03');
  const [newCloseDate, setNewCloseDate] = useState('2026-05-03');
  const [savingSettings, setSavingSettings] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState<{ level: string; category: string }>({ level: '', category: '' });

  const DESIGNATION_SHORT: Record<string, string> = {
    'President 2025-26': 'Pres\'26',
    'President Elect(2026-27)': 'PE\'27',
    'Treasurer 2026-27': 'Treas',
    'Secretary elect 2026-27': 'Secy',
    'TRF Chair 2026-27': 'TRF',
  };

  const handleToggleClubParticipation = async (clubName: string, closed: boolean) => {
    const proceed = closed
      ? window.confirm(`Mark ${clubName} as not participating? It will be treated as completed.`)
      : window.confirm(`Reopen ${clubName} for participation?`);
    if (!proceed) return;

    setClubParticipationUpdating(prev => ({ ...prev, [clubName]: true }));
    try {
      const resp = await fetch(`${API_URL}/admin/clubs/participation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ club_name: clubName, closed }),
      });
      if (!resp.ok) {
        alert('Failed to update participation status');
        return;
      }
      fetchDistrictReport();
    } catch {
      alert('Failed to update participation status');
    } finally {
      setClubParticipationUpdating(prev => ({ ...prev, [clubName]: false }));
    }
  };

  const handleExportDesignationExcel = async (view: 'designation' | 'club' | 'dd') => {
    try {
      const endpoint = view === 'dd' 
        ? `${API_URL}/admin/export-dd-wise-excel`
        : `${API_URL}/admin/export-designation-excel?view=${view}`;
      const blob = await fetch(endpoint).then(r => r.blob());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DLA2026_${view}_report_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting designation Excel:', error);
      alert('Failed to export Excel file');
    }
  };

  const handleExportRecentTransactionsExcel = async () => {
    try {
      const blob = await fetch(`${API_URL}/admin/export-recent-transactions-excel`).then(r => r.blob());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DLA2026_Recent_Transactions_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting recent transactions Excel:', error);
      alert('Failed to export Excel file');
    }
  };

  interface DistrictClub {
    name: string; ggr: string | null; status: 'completed' | 'partial' | 'not_registered';
    participation_closed?: boolean;
    required_present: string[]; required_missing: string[];
    registration_count?: number; total_delegates?: number;
  }
  interface AG { name: string; phone: string | null; reminder_sent_today: boolean; total: number; completed: number; partial: number; not_registered: number; clubs: DistrictClub[]; }
  interface DD { name: string; assistant_governors: AG[]; }
  interface ZoneReport { zone: number; district_directors: DD[]; }
  interface AgListItem { assistant_governor: string; district_director: string; zone: number; }

  const [districtReport, setDistrictReport] = useState<ZoneReport[]>([]);
  const [agList, setAgList] = useState<AgListItem[]>([]);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [districtFilter, setDistrictFilter] = useState('');
  const [designationView, setDesignationView] = useState<'designation' | 'club' | 'dd'>('designation');
  const [ddWiseData, setDdWiseData] = useState<any>({});
  const [expandedDDs, setExpandedDDs] = useState<Record<string, boolean>>({});
  const [expandedAGs, setExpandedAGs] = useState<Record<string, boolean>>({});
  const [reminderStatus, setReminderStatus] = useState<Record<string, 'idle' | 'sending' | 'sent' | 'cooldown'>>({});
  const [clubParticipationUpdating, setClubParticipationUpdating] = useState<Record<string, boolean>>({});
  const [clubListModalType, setClubListModalType] = useState<null | 'pending' | 'closed'>(null);
  const [manualEntryModalOpen, setManualEntryModalOpen] = useState(false);
  const [manualPayForm, setManualPayForm] = useState({
    club_name: '',
    delegate_name: '',
    delegate_designation: 'President 2025-26',
    email: '',
    phone: '',
    payment_mode: 'CASH',
    payment_reference: '',
  });
  const [markingPaid, setMarkingPaid] = useState(false);
  const REFRESH_INTERVAL = 300;
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<{ reconciled: {name:string;receipt_no:string}[]; failed: {name:string;reason:string}[]; total_checked: number } | null>(null);

  const isViewer = sessionStorage.getItem('adminRole') === 'viewer';
  const userRole = sessionStorage.getItem('adminRole') || 'admin';
  const formatISTDateTime = (value: string) => {
    const normalized = String(value || '').trim().replace(' ', 'T');
    const utcValue = normalized.endsWith('Z') ? normalized : `${normalized}Z`;
    const date = new Date(utcValue);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };
  const designationReport: DesignationReportItem[] = Object.values(
    registrations.reduce((acc, registration) => {
      registration.delegates.forEach((delegate) => {
        const designation = delegate.delegate_designation || 'Unknown';
        if (!acc[designation]) {
          acc[designation] = {
            designation,
            total_registered: 0,
            registrations: [],
          };
        }
        acc[designation].total_registered += 1;
        acc[designation].registrations.push({
          person_name: delegate.delegate_name,
          club_name: registration.club_name,
        });
      });
      return acc;
    }, {} as Record<string, DesignationReportItem>)
  ).sort((a, b) => a.designation.localeCompare(b.designation));
  const clubDesignationReport: ClubDesignationReportItem[] = Object.values(
    registrations.reduce((acc, registration) => {
      const clubName = registration.club_name || 'Unknown';
      if (!acc[clubName]) {
        acc[clubName] = {
          club_name: clubName,
          total_registered: 0,
          registrations: [],
        };
      }
      registration.delegates.forEach((delegate) => {
        acc[clubName].total_registered += 1;
        acc[clubName].registrations.push({
          person_name: delegate.delegate_name,
          designation: delegate.delegate_designation,
        });
      });
      return acc;
    }, {} as Record<string, ClubDesignationReportItem>)
  ).sort((a, b) => a.club_name.localeCompare(b.club_name));
  const districtClubNames = Array.from(new Set(
    districtReport.flatMap(zone =>
      zone.district_directors.flatMap(dd =>
        dd.assistant_governors.flatMap(ag => ag.clubs.map(club => club.name))
      )
    )
  )).sort((a, b) => a.localeCompare(b));
  const districtClubs = districtReport.flatMap(zone =>
    zone.district_directors.flatMap(dd =>
      dd.assistant_governors.flatMap(ag => ag.clubs)
    )
  );
  const closedClubNames = Array.from(new Set(
    districtClubs.filter(club => club.participation_closed).map(club => club.name)
  )).sort((a, b) => a.localeCompare(b));
  const registeredActiveClubCount = districtClubs.filter(
    club => !club.participation_closed && club.status !== 'not_registered'
  ).length;
  const pendingClubNames = Array.from(new Set(
    districtClubs
      .filter(club => !club.participation_closed && club.status === 'not_registered')
      .map(club => club.name)
  )).sort((a, b) => a.localeCompare(b));
  const pendingClubCount = Math.max(0, districtClubs.length - registeredActiveClubCount - closedClubNames.length);

  const fetchRegistrationCloseDate = async () => {
    try {
      const res = await fetch(`${API_URL}/settings/registration-close-date`).then(r => r.json());
      const closeDate = res.registration_close_date_ist || '2026-05-03';
      setRegistrationCloseDate(closeDate);
      setNewCloseDate(closeDate);
    } catch (error) {
      console.error('Error fetching registration close date:', error);
    }
  };

  const fetchDdWiseReport = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/dd-wise-report`).then(r => r.json());
      if (res.success) {
        setDdWiseData(res.data);
      }
    } catch (error) {
      console.error('Error fetching DD-wise report:', error);
    }
  };

  const handleSaveCloseDate = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newCloseDate)) {
      alert('Invalid date format. Use YYYY-MM-DD');
      return;
    }
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/admin/settings/registration-close-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: newCloseDate }),
      });
      if (res.ok) {
        setRegistrationCloseDate(newCloseDate);
        alert('Registration closure date updated successfully!');
      } else {
        alert('Failed to update registration closure date');
      }
    } catch (error) {
      console.error('Error updating registration close date:', error);
      alert('Failed to update registration closure date');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (logFilter.level) params.append('level', logFilter.level);
      if (logFilter.category) params.append('category', logFilter.category);
      params.append('limit', '200');
      
      const response = await fetch(`${API_URL}/admin/logs?${params}`);
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const clearOldLogs = async (days: number) => {
    if (!window.confirm(`Delete logs older than ${days} days?`)) return;
    try {
      await fetch(`${API_URL}/admin/logs?days=${days}`, { method: 'DELETE' });
      fetchLogs();
    } catch (error) {
      alert('Failed to clear logs');
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem('adminLoggedIn')) {
      navigate('/admin-login');
      return;
    }
    fetchDashboardData();
    fetchTransactions(1);
    fetchDistrictReport();
    fetchRegistrationCloseDate();
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, logFilter]);

  useEffect(() => {
    if (isViewer && activeTab === 'overview') {
      setActiveTab('registrations');
    }
  }, [isViewer, activeTab]);

  useEffect(() => {
    const tick = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchDashboardData();
          fetchTransactions(1);
          fetchDistrictReport();
          fetch(`${API_URL}/admin/reconcile-payments`, { method: 'POST' }).catch(() => {});
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const handleReconcile = async () => {
    setReconciling(true);
    setReconcileResult(null);
    try {
      const res = await fetch(`${API_URL}/admin/reconcile-payments`, { method: 'POST' });
      const data = await res.json();
      setReconcileResult(data);
      if (data.reconciled?.length > 0) {
        fetchDashboardData();
        fetchTransactions(1);
      }
    } catch {
      alert('Failed to sync payments');
    } finally {
      setReconciling(false);
    }
  };

  const sendAgReminder = async (agName: string) => {
    setReminderStatus(prev => ({ ...prev, [agName]: 'sending' }));
    try {
      const resp = await fetch(`${API_URL}/admin/send-ag-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ag_name: agName }),
      });
      if (resp.status === 429) {
        setReminderStatus(prev => ({ ...prev, [agName]: 'cooldown' }));
      } else if (resp.ok) {
        setReminderStatus(prev => ({ ...prev, [agName]: 'sent' }));
      } else {
        setReminderStatus(prev => ({ ...prev, [agName]: 'idle' }));
        alert('Failed to send reminder');
      }
    } catch {
      setReminderStatus(prev => ({ ...prev, [agName]: 'idle' }));
      alert('Failed to send reminder');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminLoggedIn');
    navigate('/admin-login');
  };

  const fetchDistrictReport = async () => {
    try {
      setDistrictLoading(true);
      const res = await fetch(`${API_URL}/admin/district-report`).then(r => r.json());
      setDistrictReport(res.report || []);
      setAgList(res.ag_list || []);
    } catch (error) {
      console.error('Error fetching district report:', error);
    } finally {
      setDistrictLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [summaryRes, registrationsRes] = await Promise.all([
        fetch(`${API_URL}/admin/summary`).then(r => r.json()),
        fetch(`${API_URL}/admin/registrations`).then(r => r.json()),
      ]);
      setSummary(summaryRes.summary);
      setRegistrations(registrationsRes.registrations);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      alert('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page: number) => {
    try {
      setTxLoading(true);
      const res = await fetch(`${API_URL}/admin/transactions?page=${page}&limit=10`).then(r => r.json());
      setRecentTransactions(res.transactions);
      setPagination(res.pagination);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setTxLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/export-excel`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DLA2026_Registrations_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error exporting Excel:', error);
      alert(`Failed to export Excel: ${error?.message || 'Unknown error'}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      success: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      created: 'bg-blue-100 text-blue-800',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getNotifBadge = (status: string, label: string) => {
    const icon = label === 'Email' ? '✉️' : '💬';
    const colorClass =
      status === 'sent' ? 'bg-green-100 text-green-800' :
      status === 'failed' ? 'bg-red-100 text-red-800' :
      'bg-gray-100 text-gray-500';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {icon} {label}: {(status || 'pending').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Manage registrations and view analytics</p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <a
                href="/"
                className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
              >
                Registration Form
              </a>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 sm:px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {!isViewer && summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-3 sm:mb-0">
                  <p className="text-xs sm:text-sm text-gray-600">Total Registrations</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{summary.totalRegistrations}</p>
                </div>
                <div className="bg-blue-100 p-2 sm:p-3 rounded-full">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-3 sm:mb-0">
                  <p className="text-xs sm:text-sm text-gray-600">Total Delegates</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{summary.totalDelegates}</p>
                </div>
                <div className="bg-purple-100 p-2 sm:p-3 rounded-full">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-3 sm:mb-0">
                  <p className="text-xs sm:text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">₹{summary.totalAmount.toLocaleString()}</p>
                </div>
                <div className="bg-green-100 p-2 sm:p-3 rounded-full">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-3 sm:mb-0">
                  <p className="text-xs sm:text-sm text-gray-600">Pending Clubs</p>
                  <button
                    type="button"
                    onClick={() => setClubListModalType('pending')}
                    className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 hover:underline"
                  >
                    {pendingClubCount}/{districtClubs.length}
                  </button>
                </div>
                <div className="bg-yellow-100 p-2 sm:p-3 rounded-full">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

          </div>
        )}

        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {!isViewer && (
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-6 py-4 text-sm font-medium ${
                    activeTab === 'overview'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Recent Transactions
                </button>
              )}
              <button
                onClick={() => setActiveTab('registrations')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'registrations'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                All Registrations
              </button>
              <button
                onClick={() => setActiveTab('designation')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'designation'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Designation Report
              </button>
              <button
                onClick={() => setActiveTab('district')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'district'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                District Report
              </button>
{/* District Contacts tab disabled
              <button
                onClick={() => setActiveTab('contacts')}
                className={`px-6 py-4 text-sm font-medium ${
                  activeTab === 'contacts'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Contacts
              </button>
*/}
              {!isViewer && (
                <button
                  onClick={() => setActiveTab('logs')}
                  className={`px-6 py-4 text-sm font-medium ${
                    activeTab === 'logs'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Logs
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-6 py-4 text-sm font-medium ${
                    activeTab === 'settings'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Settings
                </button>
              )}
            </nav>
          </div>

          <div className="p-6">
            {!isViewer && activeTab === 'overview' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Recent Transactions</h2>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleExportRecentTransactionsExcel}
                      className="flex items-center space-x-2 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span>Export Excel</span>
                    </button>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                      Refresh in {String(Math.floor(countdown / 60)).padStart(2, '0')}:{String(countdown % 60).padStart(2, '0')}
                    </span>
                    {!isViewer && <button
                      onClick={handleReconcile}
                      disabled={reconciling}
                      className="flex items-center gap-2 bg-orange-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-orange-600 disabled:opacity-50"
                    >
                      {reconciling ? (
                        <><span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full"></span> Syncing…</>
                      ) : '🔄 Sync PG'}
                    </button>}
                  </div>
                </div>
                {reconcileResult && (
                  <div className={`mb-4 p-3 rounded-lg text-xs border ${reconcileResult.reconciled.length > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    {reconcileResult.reconciled.length > 0 ? (
                      <>
                        <p className="font-semibold text-green-800 mb-1">✅ {reconcileResult.reconciled.length} payment(s) reconciled:</p>
                        {reconcileResult.reconciled.map((r, i) => (
                          <p key={i} className="text-green-700">{r.name} → {r.receipt_no}</p>
                        ))}
                      </>
                    ) : (
                      <p className="text-gray-500">✓ Checked {reconcileResult.total_checked ?? 0} pending order(s) — no new captures found.</p>
                    )}
                  </div>
                )}
                {txLoading ? (
                  <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>
                ) : (
                  <>
                    {/* Mobile Card View */}
                    <div className="block sm:hidden space-y-4">
                      {recentTransactions.map((transaction) => (
                        <div key={transaction.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          {transaction.receipt_no && (
                            <p className="text-xs font-bold text-blue-700 mb-2">Receipt: {transaction.receipt_no}</p>
                          )}
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="font-semibold text-gray-900">{transaction.name}</h3>
                              <p className="text-sm text-gray-600">{transaction.club_name}</p>
                            </div>
                            <div className="text-right">
                              {getStatusBadge(transaction.status)}
                              <p className="text-lg font-bold text-gray-900 mt-1">₹{transaction.amount.toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 space-y-1">
                            <p><span className="font-medium">Payment ID:</span> {transaction.razorpay_payment_id || '-'}</p>
                            <p><span className="font-medium">Date:</span> {formatISTDateTime(transaction.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt No</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Club</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {recentTransactions.map((transaction) => (
                            <tr key={transaction.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-blue-700">{transaction.receipt_no || '-'}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.name}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.club_name}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">₹{transaction.amount.toLocaleString()}</td>
                              <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(transaction.status)}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs">{transaction.razorpay_payment_id || '-'}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatISTDateTime(transaction.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => fetchTransactions(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md">
                          {pagination.page} / {pagination.totalPages}
                        </span>
                        <button
                          onClick={() => fetchTransactions(pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'district' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">District Report</h2>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={districtFilter}
                      onChange={(e) => setDistrictFilter(e.target.value)}
                      placeholder="🔍 Filter by club or AG..."
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => {
                        const rows = [['Zone', 'District Director', 'Assistant Governor', 'GGR', 'Club', 'Status', 'Participation Closed', 'Designations Present', 'Designations Missing']];
                        districtReport.forEach(z => z.district_directors.forEach(dd => dd.assistant_governors.forEach(ag => ag.clubs.forEach(c => {
                          rows.push([
                            String(z.zone),
                            dd.name,
                            ag.name,
                            c.ggr || '',
                            c.name,
                            c.status,
                            c.participation_closed ? 'Yes' : 'No',
                            c.required_present.join('; '),
                            c.required_missing.join('; '),
                          ]);
                        }))));
                        const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url;
                        a.download = `district-report-${new Date().toISOString().slice(0,10)}.csv`;
                        a.click();
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700"
                    >
                      📥 Export CSV
                    </button>
                  </div>
                </div>

                {!isViewer && (
                  <div className="mb-4">
                    <button
                      onClick={() => setManualEntryModalOpen(true)}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700"
                    >
                      + Manual Entry
                    </button>
                  </div>
                )}

                {districtLoading ? (
                  <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>
                ) : districtReport.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No district data available.</p>
                ) : (
                  <div className="space-y-6">
                    {districtReport.map(zone => (
                      <div key={zone.zone}>
                        <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3">Zone {zone.zone}</h3>
                        <div className="space-y-4">
                          {zone.district_directors.map(dd => {
                            const ddTotal = dd.assistant_governors.reduce((s, ag) => s + ag.total, 0);
                            const ddCompleted = dd.assistant_governors.reduce((s, ag) => s + ag.completed, 0);
                            const ddNot = dd.assistant_governors.reduce((s, ag) => s + ag.not_registered + ag.partial, 0);
                            return (
                              <div key={dd.name} className="border border-gray-200 rounded-xl p-4 shadow-sm bg-white">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="font-semibold text-gray-900 text-base">District Director: {dd.name}</span>
                                  <div className="flex gap-3">
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{ddCompleted} Completed</span>
                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{ddNot} Pending</span>
                                    <span className="text-xs text-gray-500">{ddTotal} clubs total</span>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  {dd.assistant_governors.map(ag => {
                                    const filterLower = districtFilter.toLowerCase();
                                    const visibleClubs = ag.clubs.filter(c =>
                                      !filterLower ||
                                      c.name.toLowerCase().includes(filterLower) ||
                                      ag.name.toLowerCase().includes(filterLower)
                                    );
                                    if (filterLower && visibleClubs.length === 0) return null;
                                    return (
                                      <div key={ag.name} className="bg-gray-50 rounded-lg p-3">
                                        <div className="flex items-center gap-3 mb-2">
                                          <span className="text-sm font-medium text-gray-800">AG: {ag.name}</span>
                                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{ag.completed}/{ag.total}</span>
                                          {ag.not_registered + ag.partial > 0 && (
                                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                              {ag.not_registered + ag.partial} pending
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                          {visibleClubs.map(club => (
                                            <span
                                              key={club.name}
                                              className={`text-xs px-2 py-1 rounded ${
                                                club.status === 'completed'
                                                  ? 'bg-green-100 text-green-700'
                                                  : club.status === 'partial'
                                                  ? 'bg-yellow-100 text-yellow-700'
                                                  : 'bg-red-100 text-red-700'
                                              }`}
                                            >
                                              {club.name} ({club.total_delegates || 0})
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'designation' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Designation Report</h2>
                  <button
                    onClick={() => handleExportDesignationExcel(designationView)}
                    className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Export Excel</span>
                  </button>
                </div>
                <div className="mb-4 border-b border-gray-200">
                  <nav className="flex -mb-px">
                    <button
                      onClick={() => setDesignationView('designation')}
                      className={`px-4 py-2 text-sm font-medium ${designationView === 'designation' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      By Designation
                    </button>
                    <button
                      onClick={() => setDesignationView('club')}
                      className={`px-4 py-2 text-sm font-medium ${designationView === 'club' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      By Club
                    </button>
                    <button
                      onClick={() => { setDesignationView('dd'); fetchDdWiseReport(); }}
                      className={`px-4 py-2 text-sm font-medium ${designationView === 'dd' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      DD Wise
                    </button>
                  </nav>
                </div>

                {designationView === 'designation' ? (
                  designationReport.length === 0 ? (
                    <p className="text-sm text-gray-500">No successful registrations found.</p>
                  ) : (
                    <div className="space-y-3">
                      {designationReport.map((item) => (
                        <div key={item.designation} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900">{item.designation}</p>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold">
                              Total: {item.total_registered}
                            </span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-1 pr-3">Person Name</th>
                                  <th className="py-1">Club Name</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.registrations.map((reg, idx) => (
                                  <tr key={`${item.designation}-${idx}`} className="border-b last:border-0">
                                    <td className="py-1 pr-3 text-gray-800">{reg.person_name}</td>
                                    <td className="py-1 text-gray-700">{reg.club_name}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : designationView === 'club' ? (
                  clubDesignationReport.length === 0 ? (
                    <p className="text-sm text-gray-500">No successful registrations found.</p>
                  ) : (
                    <div className="space-y-3">
                      {clubDesignationReport.map((item) => (
                        <div key={item.club_name} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900">{item.club_name}</p>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold">
                              Total: {item.total_registered}
                            </span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-1 pr-3">Person Name</th>
                                  <th className="py-1">Designation</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.registrations.map((reg, idx) => (
                                  <tr key={`${item.club_name}-${idx}`} className="border-b last:border-0">
                                    <td className="py-1 pr-3 text-gray-800">{reg.person_name}</td>
                                    <td className="py-1 text-gray-700">{reg.designation}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : designationView === 'dd' ? (
                  Object.keys(ddWiseData).length === 0 ? (
                    <p className="text-sm text-gray-500">No data available. Loading...</p>
                  ) : (
                    <div className="space-y-6">
                      {Object.keys(ddWiseData).sort().map((dd) => (
                        <div key={dd} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                          <h3 className="text-lg font-bold text-gray-900 mb-4">District Director: {dd}</h3>
                          <div className="space-y-4">
                            {Object.keys(ddWiseData[dd]).sort().map((ag) => (
                              <div key={`${dd}-${ag}`} className="border border-gray-200 rounded-lg p-3 bg-white">
                                <h4 className="text-md font-semibold text-gray-800 mb-3">AG: {ag}</h4>
                                <div className="space-y-3">
                                  {Object.keys(ddWiseData[dd][ag]).sort().map((ggr) => (
                                    <div key={`${dd}-${ag}-${ggr}`}>
                                      {ggr && <p className="text-sm font-medium text-gray-700 mb-2">GGR: {ggr}</p>}
                                      <div className="space-y-2">
                                        {Object.keys(ddWiseData[dd][ag][ggr]).sort().map((club) => (
                                          <div key={`${dd}-${ag}-${ggr}-${club}`} className="border-l-4 border-blue-400 pl-3 py-2 bg-blue-50">
                                            <p className="text-sm font-semibold text-gray-900 mb-1">{club}</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                              {ddWiseData[dd][ag][ggr][club].map((delegate: any, idx: number) => (
                                                <div key={idx} className="text-xs text-gray-700">
                                                  <span className="font-medium">{delegate.designation}:</span> {delegate.name}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : null}
              </div>
            )}

            {activeTab === 'registrations' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">All Registrations</h2>
                  <button
                    onClick={handleExportExcel}
                    className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Export Excel</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {registrations.map((registration) => (
                    <div key={registration.id} className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 space-y-3 sm:space-y-0">
                        <div>
                          {registration.receipt_no && (
                            <p className="text-xs font-bold text-blue-700 mb-1">Receipt: {registration.receipt_no}</p>
                          )}
                          <h3 className="text-lg font-semibold text-gray-900">{registration.name}</h3>
                          <p className="text-sm text-gray-600">{registration.email} • {registration.phone}</p>
                          <p className="text-sm text-gray-600 mt-1">Club: {registration.club_name}</p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(registration.payment_status)}
                          <p className="text-sm text-gray-500 mt-2">
                            {formatISTDateTime(registration.created_at)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs text-gray-500">Delegates</p>
                          <p className="text-lg font-semibold text-gray-900">{registration.delegate_count}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs text-gray-500">Total Amount</p>
                          <p className="text-lg font-semibold text-gray-900">₹{registration.total_amount.toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Delegate Details:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-2">
                          {registration.delegates.map((delegate, index) => (
                            <div key={index} className="bg-blue-50 rounded p-3">
                              <p className="text-sm font-medium text-gray-900">{delegate.delegate_name}</p>
                              <p className="text-xs text-gray-600">{delegate.delegate_designation}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2 items-center">
                        {getNotifBadge(registration.email_status, 'Email')}
                        {getNotifBadge(registration.whatsapp_status, 'WhatsApp')}
                        {registration.razorpay_payment_id && (
                          <p className="text-xs text-gray-500 break-all ml-auto">Payment ID: <span className="font-mono">{registration.razorpay_payment_id}</span></p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'contacts' && <DistrictContacts userRole={userRole} />}

            {activeTab === 'logs' && !isViewer && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">System Logs</h2>
                  <div className="flex items-center gap-3">
                    <select
                      value={logFilter.level}
                      onChange={(e) => setLogFilter({ ...logFilter, level: e.target.value })}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                    >
                      <option value="">All Levels</option>
                      <option value="debug">Debug</option>
                      <option value="info">Info</option>
                      <option value="warn">Warning</option>
                      <option value="error">Error</option>
                    </select>
                    <select
                      value={logFilter.category}
                      onChange={(e) => setLogFilter({ ...logFilter, category: e.target.value })}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                    >
                      <option value="">All Categories</option>
                      <option value="registration">Registration</option>
                      <option value="registration-form">Registration Form</option>
                      <option value="payment">Payment</option>
                    </select>
                    <button
                      onClick={fetchLogs}
                      className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700"
                    >
                      Refresh
                    </button>
                    <button
                      onClick={() => clearOldLogs(7)}
                      className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-red-700"
                    >
                      Clear Old (7d)
                    </button>
                  </div>
                </div>

                {logsLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading logs...</div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No logs found</div>
                ) : (
                  <div className="bg-gray-900 rounded-lg p-4 max-h-[600px] overflow-y-auto font-mono text-sm">
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className={`py-1 border-b border-gray-800 ${
                          log.level === 'error' ? 'text-red-400' :
                          log.level === 'warn' ? 'text-yellow-400' :
                          log.level === 'info' ? 'text-green-400' :
                          'text-gray-400'
                        }`}
                      >
                        <span className="text-gray-500">{new Date(log.timestamp).toLocaleString()}</span>
                        {' '}
                        <span className={`uppercase font-bold ${
                          log.level === 'error' ? 'text-red-500' :
                          log.level === 'warn' ? 'text-yellow-500' :
                          log.level === 'info' ? 'text-green-500' :
                          'text-gray-500'
                        }`}>[{log.level}]</span>
                        {' '}
                        <span className="text-blue-400">[{log.category}]</span>
                        {' '}
                        <span className="text-white">{log.message}</span>
                        {log.details && (
                          <div className="text-gray-500 pl-4 text-xs mt-1">
                            {JSON.stringify(log.details, null, 2)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && !isViewer && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">System Settings</h2>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Registration Closure Date</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Set the date when registrations will automatically close. The date is in Indian Standard Time (IST).
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Closure Date (IST)
                      </label>
                      <div className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 font-medium">
                        {registrationCloseDate}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Closure Date (YYYY-MM-DD)
                      </label>
                      <input
                        type="date"
                        value={newCloseDate}
                        onChange={(e) => setNewCloseDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Registrations will close at 12:00 AM IST on this date
                      </p>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <button
                        onClick={handleSaveCloseDate}
                        disabled={savingSettings || newCloseDate === registrationCloseDate}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingSettings ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={() => setNewCloseDate(registrationCloseDate)}
                        disabled={savingSettings}
                        className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50"
                      >
                        Reset
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">Note:</p>
                        <p>Changes to the registration closure date take effect immediately. Users will see the updated date on the registration form.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manual Entry Modal */}
      {manualEntryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Mark Member as Paid</h3>
                <button
                  onClick={() => setManualEntryModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  value={manualPayForm.club_name}
                  onChange={(e) => setManualPayForm(prev => ({ ...prev, club_name: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Club</option>
                  {districtClubNames.map(club => (
                    <option key={club} value={club}>{club}</option>
                  ))}
                </select>
                <select
                  value={manualPayForm.delegate_designation}
                  onChange={(e) => setManualPayForm(prev => ({ ...prev, delegate_designation: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="President 2025-26">President 2025-26</option>
                  <option value="President Elect(2026-27)">President Elect(2026-27)</option>
                  <option value="Treasurer 2026-27">Treasurer 2026-27</option>
                  <option value="Secretary elect 2026-27">Secretary elect 2026-27</option>
                  <option value="TRF Chair 2026-27">TRF Chair 2026-27</option>
                  <option value="Member">Member</option>
                  <option value="Rotaract">Rotaract</option>
                  <option value="Assistant Governor">Assistant Governor</option>
                  <option value="Governor Group Representative">Governor Group Representative</option>
                  <option value="District Director">District Director</option>
                  <option value="District official">District official</option>
                </select>
                <select
                  value={manualPayForm.payment_mode}
                  onChange={(e) => setManualPayForm(prev => ({ ...prev, payment_mode: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="NEFT">NEFT</option>
                  <option value="CASH">CASH</option>
                  <option value="QR">QR</option>
                </select>
                <input
                  type="text"
                  value={manualPayForm.delegate_name}
                  onChange={(e) => setManualPayForm(prev => ({ ...prev, delegate_name: e.target.value }))}
                  placeholder="Person Name"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <input
                  type="email"
                  value={manualPayForm.email}
                  onChange={(e) => setManualPayForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Email"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <input
                  type="text"
                  value={manualPayForm.payment_reference}
                  onChange={(e) => setManualPayForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                  placeholder="Payment Reference (mandatory)"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <input
                  type="tel"
                  value={manualPayForm.phone}
                  onChange={(e) => setManualPayForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Phone"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setManualEntryModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!manualPayForm.club_name || !manualPayForm.delegate_name || !manualPayForm.email || !manualPayForm.phone || !manualPayForm.payment_reference) {
                      alert('Please fill all fields');
                      return;
                    }
                    try {
                      setMarkingPaid(true);
                      const res = await fetch(`${API_URL}/admin/manual-designation-payment`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(manualPayForm),
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        alert(data.error || 'Failed to mark as paid');
                        return;
                      }
                      alert(`Marked paid. Receipt: ${data.receipt_no} | Email: ${data.email_status} | WhatsApp: ${data.whatsapp_status}`);
                      setManualPayForm(prev => ({ ...prev, delegate_name: '', email: '', phone: '', payment_reference: '' }));
                      setManualEntryModalOpen(false);
                      fetchDashboardData();
                      fetchDistrictReport();
                      fetchTransactions(1);
                    } catch (error) {
                      console.error('Manual designation payment failed:', error);
                      alert('Failed to mark as paid');
                    } finally {
                      setMarkingPaid(false);
                    }
                  }}
                  disabled={markingPaid}
                  className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
                >
                  {markingPaid ? 'Saving…' : 'Mark Paid'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {clubListModalType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl p-6 relative">
            <button
              onClick={() => setClubListModalType(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pr-8">
              {clubListModalType === 'pending' ? 'Pending Clubs' : 'Not Participating Clubs'}
            </h3>
            <div className="max-h-[55vh] overflow-y-auto border border-gray-100 rounded-lg">
              {(clubListModalType === 'pending' ? pendingClubNames : closedClubNames).length === 0 ? (
                <p className="text-sm text-gray-500 p-4">No clubs found.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {(clubListModalType === 'pending' ? pendingClubNames : closedClubNames).map((clubName, index) => (
                    <li key={clubName} className="px-4 py-2.5 text-sm text-gray-800 flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-6">{index + 1}.</span>
                      <span>{clubName}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
